/* ─── A1CW PDP scripts ─────────────────────────────────────
   Gallery, variant pills (Web Component), tabs, add-to-cart popup.
   ─────────────────────────────────────────── */

(function () {
  'use strict';

  // ═══ Gallery ══════════════════════════════════════
  document.querySelectorAll('[data-pdp-gallery]').forEach(function (gallery) {
    const slides = gallery.querySelectorAll('[data-gallery-slide]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    if (!slides.length || !thumbs.length) return;

    function showSlide(targetId) {
      slides.forEach(function (s) {
        const match = s.dataset.mediaId === String(targetId);
        s.hidden = !match;
        s.classList.toggle('is-active', match);
      });
      thumbs.forEach(function (t) {
        const match = t.dataset.target === String(targetId);
        t.classList.toggle('is-active', match);
        t.setAttribute('aria-selected', match ? 'true' : 'false');
      });
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () { showSlide(thumb.dataset.target); });
    });

    // expose so variant changes can switch slides
    gallery.showSlide = showSlide;
  });

  // ═══ Variant pills (Web Component) ═══════════════════
  class VariantPills extends HTMLElement {
    connectedCallback() {
      try {
        const json = this.querySelector('[data-variants-json]');
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        console.error('VariantPills: invalid variants JSON', e);
        this.variants = [];
      }
      this.pills = Array.from(this.querySelectorAll('.pdp-pill'));
      this.hiddenInput = this.querySelector('[data-variant-id-input]');
      this.selected = this.readCurrentSelection();
      this.pills.forEach(p => p.addEventListener('click', this.onPillClick.bind(this)));
      this.applyDisabledStates();
    }

    readCurrentSelection() {
      const groups = this.querySelectorAll('.pdp-variants__group');
      const selection = [];
      groups.forEach(group => {
        const pos = parseInt(group.dataset.optionPosition, 10) - 1;
        const checked = group.querySelector('.pdp-pill[aria-checked="true"]');
        selection[pos] = checked ? checked.dataset.optionValue : null;
      });
      return selection;
    }

    onPillClick(e) {
      const pill = e.currentTarget;
      if (pill.getAttribute('aria-disabled') === 'true') return;
      const pos = parseInt(pill.dataset.optionPosition, 10) - 1;
      const val = pill.dataset.optionValue;

      const group = pill.closest('.pdp-variants__group');
      group.querySelectorAll('.pdp-pill').forEach(p => p.setAttribute('aria-checked', 'false'));
      pill.setAttribute('aria-checked', 'true');

      const legend = group.querySelector('[data-selected-for]');
      if (legend) legend.textContent = val;

      this.selected[pos] = val;
      this.applyDisabledStates();
      this.resolveVariant();
    }

    applyDisabledStates() {
      this.pills.forEach(pill => {
        const pos = parseInt(pill.dataset.optionPosition, 10) - 1;
        const val = pill.dataset.optionValue;
        const hypothetical = this.selected.slice();
        hypothetical[pos] = val;
        const match = this.variants.find(v => v.options.every(function (opt, i) {
          return hypothetical[i] == null || opt === hypothetical[i];
        }));
        // Only combinations that don't exist are disabled. Unpurchasable variants ($0 / quote
        // configs such as Heat Recovery) stay selectable: the CTA switches to Request Quote.
        pill.setAttribute('aria-disabled', match ? 'false' : 'true');
      });
    }

    resolveVariant() {
      const match = this.variants.find(v => v.options.every((opt, i) => opt === this.selected[i]));
      if (!match) return;
      if (this.hiddenInput) this.hiddenInput.value = match.id;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: match } }));
    }
  }
  if (!customElements.get('variant-pills')) customElements.define('variant-pills', VariantPills);

  // ═══ Variant change → update price, stock, gallery ══
  function formatMoney(cents) {
    try {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // ═══ Variant-aware specs ═══════════════════════════
  // variant.specs is resolved server-side (variant metafield → product metafield
  // fallback) by snippets/product-variant-pills.liquid. The helpers below mirror
  // the Liquid filters exactly so a JS-swapped value is byte-identical to the
  // server-rendered one.

  // Liquid `remove_first: sub` — drops the FIRST occurrence anywhere, not a suffix.
  function removeFirst(s, sub) {
    const i = s.indexOf(sub);
    return i === -1 ? s : s.slice(0, i) + s.slice(i + sub.length);
  }
  // Liquid `{% if v == v | plus: 0 | append: '' %}` — append the unit only when the
  // value is purely numeric (never "5 HP HP", never "1,050 lb lbs").
  function isNumericLiquid(s) {
    return String(Number(s)) === s;
  }

  const CHIP_STRIP = {
    horsepower: [' HP', ' hp'],
    cfm_at_psi: [' CFM', ' cfm'],
    max_psi:    [' PSI', ' psi']
  };
  const ROW_UNIT = { horsepower: ' HP', max_psi: ' PSI', noise_db: ' dB', weight_lbs: ' lbs' };

  function applyVariantSpecs(specs) {
    if (!specs) return;

    document.querySelectorAll('[data-spec-chip]').forEach(function (chip) {
      const key = chip.getAttribute('data-spec-chip');
      const raw = specs[key];
      if (raw == null || raw === '') { chip.style.display = 'none'; return; }
      let val = String(raw).trim();
      (CHIP_STRIP[key] || []).forEach(function (sub) { val = removeFirst(val, sub); });
      const valEl = chip.querySelector('.val');
      if (valEl) valEl.textContent = val;
      chip.style.display = '';
    });

    document.querySelectorAll('[data-spec-row]').forEach(function (row) {
      const key = row.getAttribute('data-spec-row');
      const raw = specs[key];
      if (raw == null || raw === '') { row.style.display = 'none'; return; }
      let val = String(raw).trim();
      if (key === 'cfm_at_psi') {
        val = removeFirst(removeFirst(val, ' CFM'), ' cfm');
      }
      const unit = ROW_UNIT[key];
      const cell = row.querySelector('[data-spec-value]');
      if (cell) cell.textContent = (unit && isNumericLiquid(val)) ? val + unit : val;
      row.style.display = '';
    });

    const modelEl = document.querySelector('[data-spec-model]');
    if (modelEl) {
      const wrap = modelEl.closest('.pdp-model') || modelEl;
      const raw = specs.model_number;
      if (raw == null || raw === '') {
        wrap.style.display = 'none';
      } else {
        modelEl.textContent = String(raw);
        wrap.style.display = '';
      }
    }
  }

  function applyCtaMode(variant) {
    const row = document.querySelector('[data-cta-row]');
    if (!row) return;
    const threshold = parseInt(row.dataset.quoteThreshold, 10) || 0;
    const price = variant.price == null ? 0 : variant.price;
    let mode = 'cart';
    if (row.hasAttribute('data-discontinued') || price === 0 || variant.available === false) mode = 'quote-only';
    else if (threshold > 0 && price >= threshold) mode = 'quote-first';
    row.dataset.ctaMode = mode;

    const cartBtn  = row.querySelector('[data-add-to-cart-btn]');
    const quoteBtn = row.querySelector('[data-quote-cta]');
    // Keep DOM order == visual order so keyboard focus follows the primary button.
    if (cartBtn && quoteBtn) {
      if (mode === 'cart') row.insertBefore(cartBtn, quoteBtn);
      else row.insertBefore(quoteBtn, cartBtn);
    }
    // 2026-10-27: keep the quote helper caption directly under Request Quote after the re-sort (the Approve app inserts its
    // button after Add to Cart, which used to leave the caption under Approve).
    // Keep the Approve financing button (the app's wrapper div around <approve-button>) attached right after Add to Cart.
    const approveWrap = Array.prototype.find.call(row.children, function (c) { return c.querySelector && c.querySelector('approve-button'); });
    if (approveWrap && cartBtn) cartBtn.after(approveWrap);
    const quoteHelper = row.querySelector('.pdp-cta-row__helper');
    if (quoteHelper && quoteBtn) quoteBtn.after(quoteHelper);
    const qty = document.querySelector('[data-pdp-qty]');
    if (qty) qty.hidden = mode === 'quote-only';
    if (cartBtn) {
      cartBtn.hidden = mode === 'quote-only';
      cartBtn.disabled = mode === 'quote-only';
      cartBtn.classList.toggle('btn--primary', mode === 'cart');
      cartBtn.classList.toggle('btn--lg', mode === 'cart');
      cartBtn.classList.toggle('btn--secondary', mode !== 'cart');
    }
    if (quoteBtn) {
      quoteBtn.classList.toggle('btn--primary', mode !== 'cart');
      quoteBtn.classList.toggle('btn--lg', mode !== 'cart');
      quoteBtn.classList.toggle('btn--secondary', mode === 'cart');
    }
  }

  document.addEventListener('variant:change', function (e) {
    const variant = e.detail.variant;
    if (!variant) return;

    // Specs — HP / CFM / PSI / Tank / Phase chips, the spec table, and the model number
    applyVariantSpecs(variant.specs);

    // Price — when the selected variant has no price (0 or null), render the
    // "Contact For Price Quote" link instead of $0.00. Wrapped in a .price
    // span so theme styling and the cfpq-watcher keep working.
    const priceEl = document.querySelector('[data-variant-price]');
    // Discontinued products keep their server-rendered "Discontinued" label instead of a price.
    if (priceEl && !document.querySelector('[data-cta-row][data-discontinued]')) {
      if (variant.price == null || variant.price === 0) {
        priceEl.innerHTML = '<span class="price tabular"><a href="/pages/contact-us" class="price__cfpq" data-open-quote-modal>Contact For Price Quote</a></span>';
      } else {
        let inner = formatMoney(variant.price);
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          inner = '<s>' + formatMoney(variant.compare_at_price) + '</s> ' + inner;
        }
        priceEl.innerHTML = '<span class="price tabular">' + inner + '</span>';
      }
    }

    // SKU
    const skuWrap = document.querySelector('[data-variant-sku]');
    const skuEl   = skuWrap ? skuWrap.querySelector('.pdp-sku__val') : null;
    if (skuWrap && skuEl) {
      if (variant.sku) {
        skuEl.textContent = variant.sku;
        skuWrap.hidden = false;
      } else {
        skuWrap.hidden = true;
      }
    }

    // Ship-time line: resolved per variant server-side (snippets/product-ship-window.liquid via the variants JSON).
    const shipText = document.querySelector('[data-ship-text]');
    if (shipText && variant.ship) shipText.textContent = variant.ship;

    // Stock indicator suppressed — A1CW dropships, inventory flag unreliable.
    // CTA mode mirrors main-product.liquid: $0 or unpurchasable -> quote only;
    // price >= threshold -> quote first; otherwise Add to Cart first.
    applyCtaMode(variant);

    // Quote modal data-variant-id sync
    document.querySelectorAll('[data-open-quote-modal]').forEach(function (t) {
      t.dataset.variantId = variant.id;
    });
    // Discontinued prefill names the SKU: swap in the newly selected variant's SKU, and refresh the
    // modal textarea only if it still holds the untouched previous prefill.
    document.querySelectorAll('[data-quote-cta][data-quote-message]').forEach(function (t) {
      const oldSku = t.dataset.quoteMessageSku || '';
      const newSku = variant.sku || '';
      if (!oldSku || oldSku === newSku) return;
      const oldMsg = t.dataset.quoteMessage;
      const newMsg = oldMsg.split(oldSku).join(newSku);
      t.dataset.quoteMessage = newMsg;
      t.dataset.quoteMessageSku = newSku;
      const ta = document.querySelector('[data-quote-modal] textarea[name="contact[message]"]');
      if (ta && ta.value === oldMsg) ta.value = newMsg;
    });

    // Gallery — switch slide
    if (variant.featured_media_id) {
      const gallery = document.querySelector('[data-pdp-gallery]');
      if (gallery && typeof gallery.showSlide === 'function') {
        gallery.showSlide(variant.featured_media_id);
      }
    }
  });

  // ═══ Description clamp toggle ══════════════════════
  document.querySelectorAll('[data-description-toggle]').forEach(function (toggle) {
    const body = document.querySelector('[data-description-body]');
    if (!body) return;
    toggle.addEventListener('click', function () {
      const clamped = body.classList.toggle('is-clamped');
      toggle.textContent = clamped ? 'Read full description' : 'Show less';
    });
  });

  // ═══ Tabs ═══════════════════════════════════════
  document.querySelectorAll('[data-pdp-tabs]').forEach(function (tabs) {
    const buttons = tabs.querySelectorAll('[data-tab-target]');
    const panels  = tabs.querySelectorAll('[data-tab-panel]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const target = btn.dataset.tabTarget;
        buttons.forEach(b => b.setAttribute('aria-selected', b === btn ? 'true' : 'false'));
        panels.forEach(p => { p.hidden = p.dataset.tabPanel !== target; });
      });
    });
  });

  // ═══ Qty +/- buttons ════════════════════════════
  document.querySelectorAll('[data-qty-decrement]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const input = btn.parentElement.querySelector('input[type="number"]');
      if (input) { input.stepDown(); input.dispatchEvent(new Event('change', { bubbles: true })); }
    });
  });
  document.querySelectorAll('[data-qty-increment]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const input = btn.parentElement.querySelector('input[type="number"]');
      if (input) { input.stepUp(); input.dispatchEvent(new Event('change', { bubbles: true })); }
    });
  });

  // ═══ Add to cart → AJAX + confirmation popup (stay on page) ══
  const pdpForm = document.getElementById('pdp-form');
  if (pdpForm) {
    pdpForm.addEventListener('submit', function (e) {
      e.preventDefault();
      // Quote-only variant (e.g. Enter pressed in the qty box): send them to the quote form, not the cart.
      const ctaRow = pdpForm.querySelector('[data-cta-row]');
      if (ctaRow && ctaRow.dataset.ctaMode === 'quote-only') {
        const q = ctaRow.querySelector('[data-quote-cta]');
        if (q) q.click();
        return;
      }
      const btn = pdpForm.querySelector('[data-add-to-cart-btn]');
      const label = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(pdpForm)
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, data: j }; }); })
        .then(function (res) {
          if (btn) { btn.disabled = false; btn.textContent = label; }
          if (!res.ok) {
            showCartNotice(null, null, (res.data && (res.data.description || res.data.message)) || 'Could not add to cart.');
            return;
          }
          return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) { showCartNotice(res.data, cart, null); });
        })
        .catch(function () {
          if (btn) { btn.disabled = false; btn.textContent = label; }
        });
    });
  }

  function escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  // "Goes well with" upsells — read the JSON block rendered by
  // snippets/product-upsells.liquid, minus variants already in the cart.
  function getUpsells(cart) {
    const el = document.querySelector('[data-upsells-json]');
    if (!el) return [];
    let items;
    try { items = JSON.parse(el.textContent) || []; }
    catch (e) { console.error('Upsells: invalid JSON', e); return []; }
    const inCart = {};
    if (cart && cart.items) cart.items.forEach(function (i) { inCart[i.variant_id] = true; });
    return items.filter(function (u) { return u && u.variantId && !inCart[u.variantId]; }).slice(0, 3);
  }

  function showCartNotice(item, cart, errorMsg) {
    const old = document.getElementById('a1-cart-notify');
    if (old) old.remove();
    const el = document.createElement('div');
    el.id = 'a1-cart-notify';
    el.className = 'cart-notify' + (errorMsg ? ' cart-notify--error' : '');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-live', 'polite');

    if (errorMsg) {
      el.innerHTML =
        '<div class="cart-notify__head"><strong>' + escapeHtml(errorMsg) + '</strong>' +
        '<button type="button" class="cart-notify__close" aria-label="Close">&times;</button></div>';
    } else {
      const img = item.image ? '<img src="' + escapeHtml(item.image) + '" alt="" class="cart-notify__img">' : '';
      const count = cart ? cart.item_count : item.quantity;

      const upsells = getUpsells(cart);
      let upsellHtml = '';
      if (upsells.length) {
        upsellHtml =
          '<div class="cart-notify__upsells"><span class="cart-notify__upsells-heading">Goes well with</span>' +
          upsells.map(function (u) {
            return '<div class="cart-notify__upsell">' +
              (u.image ? '<img src="' + escapeHtml(u.image) + '" alt="" class="cart-notify__upsell-img">' : '') +
              '<div class="cart-notify__upsell-info">' +
              '<a href="' + escapeHtml(u.url) + '">' + escapeHtml(u.title) + '</a>' +
              '<span class="cart-notify__upsell-price">' + formatMoney(u.price) + '</span>' +
              '</div>' +
              '<button type="button" class="btn btn--secondary btn--sm" data-upsell-add="' + escapeHtml(String(u.variantId)) + '">Add</button>' +
              '</div>';
          }).join('') +
          '</div>';
      }

      el.innerHTML =
        '<div class="cart-notify__head"><span class="cart-notify__check" aria-hidden="true">✓</span>' +
        '<strong>Added to cart</strong>' +
        '<button type="button" class="cart-notify__close" aria-label="Close">&times;</button></div>' +
        '<div class="cart-notify__body">' + img +
        '<div class="cart-notify__info"><span class="cart-notify__title">' + escapeHtml(item.product_title || item.title) + '</span>' +
        '<span class="cart-notify__meta">Qty ' + escapeHtml(item.quantity) + '</span></div></div>' +
        upsellHtml +
        '<div class="cart-notify__actions">' +
        '<a class="btn btn--primary btn--block" href="/checkout">Check out</a>' +
        '<a class="btn btn--secondary btn--block" data-view-cart-link href="/cart">View cart (' + count + ')</a>' +
        '<button type="button" class="btn btn--ghost btn--block" data-cart-continue>Continue shopping</button>' +
        '</div>';
      document.querySelectorAll('[data-cart-count]').forEach(function (n) { n.textContent = count; });
    }

    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('is-open'); });

    function close() {
      el.classList.remove('is-open');
      setTimeout(function () { if (el.parentNode) el.remove(); }, 250);
    }
    const closeBtn = el.querySelector('.cart-notify__close');
    if (closeBtn) closeBtn.addEventListener('click', close);
    const cont = el.querySelector('[data-cart-continue]');
    if (cont) cont.addEventListener('click', close);

    el.querySelectorAll('[data-upsell-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearTimeout(window.__a1CartTimer); // interacting cancels auto-close
        btn.disabled = true;
        const orig = btn.textContent;
        btn.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(btn.dataset.upsellAdd), quantity: 1 })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
          })
          .then(function (r) { return r.json(); })
          .then(function (c) {
            btn.textContent = '✓ Added';
            btn.classList.add('is-added');
            document.querySelectorAll('[data-cart-count]').forEach(function (n) { n.textContent = c.item_count; });
            const viewCart = el.querySelector('[data-view-cart-link]');
            if (viewCart) viewCart.textContent = 'View cart (' + c.item_count + ')';
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = orig;
          });
      });
    });

    clearTimeout(window.__a1CartTimer);
    const hasUpsells = !!el.querySelector('[data-upsell-add]');
    window.__a1CartTimer = setTimeout(close, hasUpsells ? 12000 : 7000);
  }
})();
